import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective, TooltipPlacement } from '@sb/tooltip';

import { LegendComponent } from '../legend/legend.component';
import type { StackedBarChartItem, StackedBarChartRestItem } from './horizontal-stacked-bar-chart.model';
import { HorizontalStackedBarTooltipItemComponent } from './horizontal-stacked-bar-tooltip-item/horizontal-stacked-bar-tooltip-item.component';

const MINIMUM_AMOUNT_TO_COMPRESS = 2;
const MINIMUM_COMPRESSIBLE_START_INDEX = 1;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-horizontal-stacked-bar-chart',
  templateUrl: './horizontal-stacked-bar-chart.component.html',
  imports: [CommonModule, TooltipDirective, HorizontalStackedBarTooltipItemComponent, LegendComponent, TranslatePipe],
})
export class HorizontalStackedBarChartComponent {
  container = viewChild('container', { read: ElementRef });
  stackedBars = viewChildren('stackedBar', { read: ElementRef });

  items = input<StackedBarChartItem[], StackedBarChartItem[]>([], {
    transform: (items) => (Array.isArray(items) ? items : []),
  });
  restItem = input<StackedBarChartRestItem>();

  restBarIndex = signal(0);
  compressedBarPosX = signal<number | null>(null);

  restPercentage = computed(() => Math.max(0, Math.min(1 - this.getCombinedPercentage(this.items()), 1)) || 0);

  restBarLabel = computed(() => {
    const restItem = this.restItem();

    return restItem ? this.barLabel(restItem) : null;
  });

  compressLegendItems = computed(() => (this.restBarIndex() ? this.items().slice(this.restBarIndex()) : []));
  visibleLegendItems = computed(() =>
    this.restBarIndex() ? this.items().slice(0, this.restBarIndex()) : this.items(),
  );

  tooltipPlacement = TooltipPlacement.LEFT;

  constructor() {
    afterRenderEffect({
      // Measures the rendered bars but writes only signals, so it belongs in the phase that runs
      // ahead of any DOM write.
      earlyRead: () => {
        // Read for its own sake: the fit depends on how wide the bars are, and a data set with the
        // same number of bars still changes their widths.
        this.items();

        const element = this.container()?.nativeElement;

        if (!element) {
          return;
        }

        this.handleStackBarFit(element);
      },
    });
  }

  handleStackBarFit(element: HTMLElement) {
    const overFlowingStackBarIndex = this.findOverFlowingStackBarIndex(element);

    if (overFlowingStackBarIndex <= 0) {
      this.restBarIndex.set(0);
      this.compressedBarPosX.set(null);

      return;
    }

    const restBarIndex = this.getRestBarStartIndex(overFlowingStackBarIndex);

    this.restBarIndex.set(restBarIndex);
    this.compressedBarPosX.set(this.getCompressBarPosX(restBarIndex));
  }

  /**
   * Finds the index of the first stack bar that does not fit fully in the container anymore.
   * @param {HTMLElement} element - The container element.
   * @return {number} -1 if all stack bars fit in the container
   */
  findOverFlowingStackBarIndex(element: HTMLElement): number {
    const containerRect = element.getBoundingClientRect();

    return this.stackedBars().findIndex(({ nativeElement: stackBar }) => {
      const { x, width } = stackBar.getBoundingClientRect();
      const barPositionX = x - containerRect.x;

      return barPositionX + width > containerRect.width;
    });
  }

  /**
   * Returns the index of the stackbar from which we should start showing the compressedBar.
   * @param {number} index - The index of the stack bar that does not fit fully in the container anymore.
   * @return {number} The index of the first stack bar that should be compressed.
   */
  getRestBarStartIndex(index: number): number {
    const highestPossibleIndex = Math.min(index, this.stackedBars().length - MINIMUM_AMOUNT_TO_COMPRESS);
    const lowestPossibleIndex = Math.max(highestPossibleIndex, MINIMUM_COMPRESSIBLE_START_INDEX);

    const visibleStackBars = this.items().slice(0, lowestPossibleIndex);
    const visiblePercentage = this.getCombinedPercentage(visibleStackBars);

    // if we have 12 bars and 10 are visible. we want to
    // - calculate the accumulated percentage of the visible bars (ecxample: 60%)
    // - calculate how many bars represent that value over the visible bars (.6 * 10 = 60% of 10 = 6 bars)
    // - return said value. 60% of stack bars will be uncompressed. 40% will be compressed.
    return Math.ceil(visiblePercentage * visibleStackBars.length);
  }

  getCompressBarPosX(index: number): number | null {
    const container = this.container()?.nativeElement;
    const stackBar = this.stackedBars()[index]?.nativeElement;

    if (!container || !stackBar) {
      return null;
    }

    return stackBar.getBoundingClientRect().x - container.getBoundingClientRect().x;
  }

  /**
   * The bars carry no text, so each one is named for screen readers by the same label and value the
   * tooltip shows. `tooltipText` arrives already formatted and localised by the consumer.
   */
  barLabel(item: StackedBarChartRestItem): string {
    return `${item.label}, ${item.tooltipText}`;
  }

  getCombinedPercentage(items: StackedBarChartItem[]): number {
    const result = items.reduce((acc, { percentage }) => acc + percentage, 0) / 100;

    return +result.toFixed(2);
  }
}
